<section class="insights" aria-labelledby="activity-title">
  <header class="insights-header">
    <div>
      <div class="eyebrow" i18n="@@activityEyebrow">PORTAL INSIGHTS</div>
      <h2 id="activity-title" i18n="@@activityTitle">Aktivität & Nutzung</h2>
      <p class="text-muted mb-0" i18n="@@activityIntro">Ein zentraler Blick auf alle Mandanten, Menschen und Tools.</p>
    </div>
    <div class="refresh-actions">
      @if (updatedAt) {
        <span class="updated-at" [class.is-live]="live">
          <span class="live-dot" aria-hidden="true"></span>
          <span i18n="@@activityUpdated">Stand: {{ updatedAt | date:'shortTime' }}</span>
        </span>
      }
      <div class="form-check form-switch mb-0">
        <input class="form-check-input" type="checkbox" role="switch" id="activity-live" [(ngModel)]="live" (change)="toggleLive()">
        <label class="form-check-label" for="activity-live" i18n="@@activityLiveShort">Live</label>
      </div>
      <button class="btn btn-icon" type="button" [disabled]="loading" (click)="refresh(false)"
        i18n-title="@@activityRefresh" title="Aktualisieren" i18n-aria-label="@@activityRefresh" aria-label="Aktualisieren">
        <i class="ri-refresh-line" [class.ri-spin]="loading" aria-hidden="true"></i>
      </button>
    </div>
  </header>

  <div class="toolbar">
    <div class="period-switch" role="group" i18n-aria-label="@@activityPeriod" aria-label="Zeitraum">
      @for (period of periods; track period) {
        <button type="button" [class.active]="activePeriod === period" [attr.aria-pressed]="activePeriod === period"
          [disabled]="loading" (click)="setPeriod(period)"><span i18n="@@activityDays">{{ period }} Tage</span></button>
      }
      @if (activePeriod === null) {
        <button type="button" class="active" aria-pressed="true" (click)="filtersOpen = true">
          {{ from | date:'dd.MM.yy':'UTC' }} – {{ to | date:'dd.MM.yy':'UTC' }}
        </button>
      }
    </div>
    <div class="chips">
      @for (chip of activeFilters; track chip.key) {
        <span class="chip">
          <span class="chip-label">{{ chip.label }}:</span> {{ chip.value }}
          <button type="button" [disabled]="loading" (click)="clearFilter(chip.key)"
            i18n-aria-label="@@activityRemoveFilter" aria-label="Filter entfernen"><i class="ri-close-line" aria-hidden="true"></i></button>
        </span>
      }
      @if (activeFilters.length > 1) {
        <button type="button" class="btn btn-link btn-sm p-0" [disabled]="loading" (click)="reset()" i18n="@@activityReset">Zurücksetzen</button>
      }
    </div>
    <button type="button" class="btn btn-sm filter-toggle" [class.open]="filtersOpen" (click)="filtersOpen = !filtersOpen"
      [attr.aria-expanded]="filtersOpen" aria-controls="activity-filters">
      <i class="ri-filter-3-line" aria-hidden="true"></i> <span i18n="@@activityFilters">Filter</span>
      @if (activeFilters.length) { <span class="filter-count">{{ activeFilters.length }}</span> }
      <i class="ri-arrow-down-s-line chevron" aria-hidden="true"></i>
    </button>
  </div>

  @if (filtersOpen) {
    <form id="activity-filters" class="card-panel filters" (ngSubmit)="refresh()">
      <label><span i18n="@@activityFrom">Von (UTC)</span><input class="form-control" type="date" name="from" [(ngModel)]="from" required></label>
      <label><span i18n="@@activityTo">Bis einschließlich (UTC)</span><input class="form-control" type="date" name="to" [(ngModel)]="to" required></label>
      <label><span i18n="@@activityEvent">Ereignis</span>
        <select class="form-select" name="kind" [(ngModel)]="kind">
          <option value="" i18n="@@activityAllEvents">Alle Ereignisse</option>
          <option value="portal_visit" i18n="@@activityVisit">Portalbesuch</option>
          <option value="tile_open" i18n="@@activityOpen">Kachel-Aufruf</option>
        </select>
      </label>
      <label><span i18n="@@activityTenant">Mandant</span>
        <select class="form-select" name="mandant" [(ngModel)]="mandantId">
          <option value="" i18n="@@activityAllTenants">Alle Mandanten</option>
          @for (item of directory.mandants; track item.id) { <option [value]="item.id">{{ item.name }}</option> }
        </select>
      </label>
      <label><span i18n="@@activityUser">Benutzer</span>
        <select class="form-select" name="user" [(ngModel)]="userId">
          <option value="" i18n="@@activityAllUsers">Alle Benutzer</option>
          @for (item of directory.users; track item.id) { <option [value]="item.id">{{ item.name }} · {{ item.email }}</option> }
        </select>
      </label>
      <label><span i18n="@@activityTile">Kachel</span>
        <select class="form-select" name="tile" [(ngModel)]="tileId">
          <option value="" i18n="@@activityAllTiles">Alle Kacheln</option>
          @for (item of directory.tiles; track item.id) { <option [value]="item.id">{{ item.name }}</option> }
        </select>
      </label>
      <div class="filter-actions">
        <button class="btn btn-outline-secondary" type="button" [disabled]="loading" (click)="reset()" i18n="@@activityReset">Zurücksetzen</button>
        <button class="btn btn-primary" type="submit" [disabled]="loading" i18n="@@activityApply">Filter anwenden</button>
      </div>
    </form>
  }

  @if (error) { <div class="alert alert-danger mb-0" role="alert">{{ error }}</div> }

  @if (loading && !report) {
    <div class="dashboard-grid" role="status">
      <span class="visually-hidden" i18n="@@activityLoading">Nutzungsdaten werden geladen …</span>
      @for (i of [1, 2, 3, 4]; track i) { <div class="skeleton kpi"></div> }
      <div class="skeleton span-8"></div>
      <div class="skeleton span-4"></div>
    </div>
  }

  @if (report; as data) {
    <div class="dashboard-grid" [class.updating]="loading" [attr.aria-busy]="loading">
      <article class="card-panel kpi kpi-visits">
        <div class="kpi-head">
          <span class="kpi-icon"><i class="ri-login-circle-line" aria-hidden="true"></i></span>
          <span class="kpi-label" i18n="@@activityVisits">Portalbesuche</span>
        </div>
        <strong class="kpi-value">{{ data.totals.visits | number }}</strong>
        <span class="kpi-sub" i18n="@@activityPerDay">Ø {{ perDay(data.totals.visits) | number:'1.0-1' }} pro Tag</span>
        <svg class="sparkline" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true"><polyline [attr.points]="sparklines.visits" /></svg>
      </article>
      <article class="card-panel kpi kpi-opens">
        <div class="kpi-head">
          <span class="kpi-icon"><i class="ri-cursor-line" aria-hidden="true"></i></span>
          <span class="kpi-label" i18n="@@activityOpens">Kachel-Aufrufe</span>
        </div>
        <strong class="kpi-value">{{ data.totals.opens | number }}</strong>
        <span class="kpi-sub" i18n="@@activityPerDay">Ø {{ perDay(data.totals.opens) | number:'1.0-1' }} pro Tag</span>
        <svg class="sparkline" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true"><polyline [attr.points]="sparklines.opens" /></svg>
      </article>
      <article class="card-panel kpi kpi-users">
        <div class="kpi-head">
          <span class="kpi-icon"><i class="ri-team-line" aria-hidden="true"></i></span>
          <span class="kpi-label" i18n="@@activityActiveUsers">Aktive Benutzer</span>
        </div>
        <strong class="kpi-value">{{ data.totals.users | number }}</strong>
        <span class="kpi-sub" i18n="@@activityOpensPerUser">Ø {{ (data.totals.users ? data.totals.opens / data.totals.users : 0) | number:'1.0-1' }} Kachel-Aufrufe pro Person</span>
      </article>
      <article class="card-panel kpi kpi-tenants">
        <div class="kpi-head">
          <span class="kpi-icon"><i class="ri-building-line" aria-hidden="true"></i></span>
          <span class="kpi-label" i18n="@@activityActiveTenants">Aktive Mandanten</span>
        </div>
        <strong class="kpi-value">{{ data.totals.mandants | number }}</strong>
        <span class="kpi-sub" i18n="@@activityOfTenants">von {{ directory.mandants.length | number }} Mandanten</span>
        <div class="kpi-meter" aria-hidden="true"><span [style.width.%]="share(data.totals.mandants, directory.mandants.length)"></span></div>
      </article>

      @if (data.totals.events === 0) {
        <div class="alert alert-info empty-note mb-0" i18n="@@activityEmpty">Für diese Filter liegen noch keine Ereignisse vor. Die Erfassung beginnt mit der Bereitstellung dieser Funktion.</div>
      }

      <article class="card-panel span-8">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityTrend">Aktivität im Zeitverlauf</h3>
            <p class="panel-hint">
              <span i18n="@@activityTrendHint">Tageswerte in UTC</span>
              @if (peakDay) { · <span i18n="@@activityPeakDay">Spitzentag {{ peakDay.date | date:'dd.MM.':'UTC' }} mit {{ peakDay.total | number }} Ereignissen</span> }
            </p>
          </div>
          <i class="ri-line-chart-line panel-icon" aria-hidden="true"></i>
        </div>
        <app-activity-trend-chart [days]="days"></app-activity-trend-chart>
      </article>

      <article class="card-panel span-4">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityTopToolsShort">Top-Kacheln</h3>
            <p class="panel-hint" i18n="@@activityDrillHint">Klicken zum Filtern</p>
          </div>
          <i class="ri-trophy-line panel-icon" aria-hidden="true"></i>
        </div>
        <app-activity-ranking [entries]="topTiles" [total]="data.totals.opens" [disabled]="loading" (pick)="drillDown('tile', $event)"></app-activity-ranking>
      </article>

      <article class="card-panel span-5">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityTopTenantsShort">Top-Mandanten</h3>
            <p class="panel-hint" i18n="@@activityTopTenantsHint">Nach Kachel-Aufrufen · klicken zum Filtern</p>
          </div>
          <i class="ri-building-2-line panel-icon" aria-hidden="true"></i>
        </div>
        <app-activity-ranking class="scroll" [entries]="topMandants" [total]="data.totals.opens" [disabled]="loading" emptyIcon="ri-building-line" (pick)="drillDown('mandant', $event)"></app-activity-ranking>
      </article>

      <article class="card-panel span-7">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityStream">Aktivitätsstream</h3>
            <p class="panel-hint" i18n="@@activityStreamHint">Neueste Ereignisse zuerst. Zeiten in deiner lokalen Zeitzone. Gelöschte Konten, Mandanten oder Kacheln erscheinen mit ihrer ID.</p>
          </div>
          <i class="ri-pulse-line panel-icon" aria-hidden="true"></i>
        </div>
        <ol class="activity-stream scroll">
          @for (event of data.events; track event._id) {
            <li [class.is-open]="event.kind === 'tile_open'">
              <i [class]="event.kind === 'tile_open' ? 'ri-cursor-line event-icon' : 'ri-login-circle-line event-icon'" aria-hidden="true"></i>
              <div class="event-body"><strong>{{ userName(event.userId) }}</strong>
                @if (event.kind === 'tile_open') { <span i18n="@@activityOpened">hat eine Kachel geöffnet:</span> <strong>{{ tileName(event.tileId) }}</strong> }
                @else { <span i18n="@@activityVisited">hat das Portal besucht.</span> }
                <div class="event-meta">{{ mandantName(event.mandantId) }}</div>
              </div>
              <time [attr.datetime]="event.at" [title]="event.at | date:'medium'">{{ event.at | date:'dd.MM. HH:mm' }}</time>
            </li>
          } @empty { <li class="empty-state"><i class="ri-pulse-line" aria-hidden="true"></i><span i18n="@@activityNoEvents">Keine Ereignisse für diese Auswahl.</span></li> }
        </ol>
        <div class="pagination-bar">
          <span i18n="@@activityEventCount">{{ data.totals.events }} Ereignisse · Seite {{ eventPage + 1 }}</span>
          <div class="pager">
            <button type="button" class="btn btn-icon" [disabled]="loading || eventPage === 0" (click)="changePage('events', -1)"
              i18n-aria-label="@@activityPrevious" aria-label="Zurück"><i class="ri-arrow-left-s-line" aria-hidden="true"></i></button>
            <button type="button" class="btn btn-icon" [disabled]="loading || (eventPage + 1) * data.pageSize >= data.totals.events" (click)="changePage('events', 1)"
              i18n-aria-label="@@activityNext" aria-label="Weiter"><i class="ri-arrow-right-s-line" aria-hidden="true"></i></button>
          </div>
        </div>
      </article>

      <article class="card-panel span-12">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityClusterTitle">Nutzung im Detail: Mandant → Benutzer → Kachel</h3>
            <p class="panel-hint" i18n="@@activityClusterHint">Absteigend nach Aufrufen. Alle Filter gelten gemeinsam; pro Seite werden bis zu 25 Kombinationen angezeigt.</p>
          </div>
          <i class="ri-node-tree panel-icon" aria-hidden="true"></i>
        </div>
        <p-table [value]="data.clusters" styleClass="p-datatable-sm" [tableStyle]="{'min-width': '42rem'}" [rowHover]="true">
          <ng-template #header><tr><th i18n="@@activityTenant">Mandant</th><th i18n="@@activityUser">Benutzer</th><th i18n="@@activityTile">Kachel</th><th class="text-end" i18n="@@activityOpens">Kachel-Aufrufe</th><th i18n="@@activityLastOpen">Letzter Aufruf</th></tr></ng-template>
          <ng-template #body let-row><tr><td>{{ mandantName(row._id.mandantId) }}</td><td>{{ userName(row._id.userId) }}</td><td>{{ tileName(row._id.tileId) }}</td><td class="text-end"><span class="count-pill">{{ row.count | number }}</span></td><td class="text-muted">{{ row.lastAt | date:'medium' }}</td></tr></ng-template>
          <ng-template #emptymessage><tr><td colspan="5" i18n="@@activityNoOpens">Keine Kachel-Aufrufe im gewählten Zeitraum.</td></tr></ng-template>
        </p-table>
        <div class="pagination-bar">
          <span i18n="@@activityClusterCount">{{ data.clusterTotal }} Kombinationen · Seite {{ clusterPage + 1 }}</span>
          <div class="pager">
            <button type="button" class="btn btn-icon" [disabled]="loading || clusterPage === 0" (click)="changePage('clusters', -1)"
              i18n-aria-label="@@activityPrevious" aria-label="Zurück"><i class="ri-arrow-left-s-line" aria-hidden="true"></i></button>
            <button type="button" class="btn btn-icon" [disabled]="loading || (clusterPage + 1) * data.pageSize >= data.clusterTotal" (click)="changePage('clusters', 1)"
              i18n-aria-label="@@activityNext" aria-label="Weiter"><i class="ri-arrow-right-s-line" aria-hidden="true"></i></button>
          </div>
        </div>
      </article>

      <article class="card-panel span-5">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityQuietToolsShort">Wenig genutzte Kacheln</h3>
            <p class="panel-hint" i18n="@@activityQuietHint">Aktueller Katalog bzw. Mandanten-Pool mit den wenigsten Aufrufen unter den gewählten Filtern. Eine Zuweisung bedeutet nicht automatisch Sichtbarkeit für jeden Benutzer.</p>
          </div>
          <i class="ri-zzz-line panel-icon" aria-hidden="true"></i>
        </div>
        <ul class="quiet-list">
          @for (entry of quietTiles; track entry.id) {
            <li><span>{{ entry.name }}</span>
              <span class="count-pill" [class.zero]="entry.count === 0">{{ entry.count | number }}</span></li>
          } @empty { <li class="empty-state"><i class="ri-layout-grid-line" aria-hidden="true"></i><span i18n="@@activityNoCatalog">Keine passenden Katalog-Kacheln.</span></li> }
        </ul>
      </article>

      <article class="card-panel span-7">
        <div class="panel-head">
          <div>
            <h3 i18n="@@activityInactive">Lange nicht angemeldet</h3>
            <p class="panel-hint" i18n="@@activityInactiveHint">Letzte Anmeldung laut Logto, nicht letzte Tool-Aktivität. Nur Mandanten- und Benutzerfilter gelten hier; fortbestehende Sitzungen können weiterhin aktiv sein.</p>
          </div>
          <label class="inactive-filter"><span class="visually-hidden" i18n="@@activityInactiveSince">Keine Anmeldung seit mindestens</span>
            <select class="form-select form-select-sm" [(ngModel)]="inactivityDays">
              @for (period of [30, 60, 90, 180, 365]; track period) { <option [ngValue]="period" i18n="@@activityInactiveDays">≥ {{ period }} Tage</option> }
            </select>
          </label>
        </div>
        <p-table [value]="inactiveUsers" [paginator]="inactiveUsers.length > 5" [rows]="5" styleClass="p-datatable-sm" [rowHover]="true">
          <ng-template #header><tr><th i18n="@@activityUser">Benutzer</th><th i18n="@@activityLastSignIn">Letzte Anmeldung</th></tr></ng-template>
          <ng-template #body let-user><tr>
            <td><div class="user-cell"><span class="avatar" aria-hidden="true">{{ (user.name || user.email || '?').charAt(0).toUpperCase() }}</span>
              <div>{{ user.name }}<div class="small text-muted">{{ user.email }}</div></div></div></td>
            <td>@if (user.lastSignInAt === null) { <span class="badge-soft warn" i18n="@@activityNever">Noch nie angemeldet</span> } @else { {{ user.lastSignInAt | date:'mediumDate' }} }</td>
          </tr></ng-template>
          <ng-template #emptymessage><tr><td colspan="2" i18n="@@activityNoInactive">Keine Konten für diese Auswahl.</td></tr></ng-template>
        </p-table>
      </article>
    </div>

    <details class="measurement-note">
      <summary><i class="ri-information-line" aria-hidden="true"></i> <span i18n="@@activityHowMeasured">So wird gemessen</span></summary>
      <p i18n="@@activityMeasurement">Ein Portalbesuch zählt pro Benutzer und aktivem Mandanten höchstens einmal je UTC-Halbstunde. Kachel-Aufrufe sind Öffnungen aus dem Portal, keine Nutzungsdauer oder Aktionen im Ziel-Tool. Private Links und direkte Tool-Zugriffe werden nicht erfasst.</p>
      <span i18n="@@activityCoverage">Erfassung seit {{ data.startedAt | date:'medium':'UTC' }} UTC · Aufbewahrung: {{ data.retentionDays }} Tage · Keine rückwirkenden Aufrufdaten.</span>
    </details>
  }
</section>
